/**
 * LegalIndexPage — public directory of every legal document.
 * Also serves as /legal (index) and /legal/:slug (specific doc).
 */
import { useParams, useLocation, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import {
  FileText, Shield, Truck, AlertTriangle, Scale, Landmark, Eye, Lock,
} from 'lucide-react';
import { LEGAL_DOCS, LEGAL_BY_SLUG, PUBLIC_SUMMARIES, EFFECTIVE_DATE, COMPANY, ROLE_STATEMENT } from '@/legal/legalContent';
import PublicSummaryDocument from '@/legal/PublicSummaryDocument';

const ICON_BY_SLUG = {
  'terms':                          FileText,
  'privacy':                        Eye,
  'shipping-terms':                 Truck,
  'restricted-prohibited-products': AlertTriangle,
  'aml-compliance':                 Scale,
  'regulatory':                     Landmark,
  'non-custodial':                  Lock,
};

const COLOR_BY_SLUG = {
  'terms':                          'from-blue-500 to-indigo-600',
  'privacy':                        'from-emerald-500 to-teal-600',
  'shipping-terms':                 'from-sky-500 to-blue-600',
  'restricted-prohibited-products': 'from-rose-500 to-red-600',
  'aml-compliance':                 'from-violet-500 to-purple-600',
  'regulatory':                     'from-amber-500 to-orange-600',
  'non-custodial':                  'from-slate-500 to-slate-700',
};

export default function LegalIndexPage() {
  const { slug: paramSlug } = useParams();
  const { pathname } = useLocation();
  // Alias routes like /privacy /terms /shipping-terms /regulatory /aml-compliance
  // /non-custodial /restricted-prohibited-products all resolve to their slug.
  const ALIAS = ['privacy', 'terms', 'shipping-terms', 'regulatory', 'aml-compliance', 'non-custodial', 'restricted-prohibited-products'];
  const pathSlug = ALIAS.find((s) => pathname === `/${s}`);
  const slug = paramSlug || pathSlug;

  if (slug) {
    const summary = PUBLIC_SUMMARIES[slug];
    const fullDoc = LEGAL_BY_SLUG[slug];
    if (!summary) {
      return (
        <div className="min-h-screen flex items-center justify-center px-5">
          <div className="text-center">
            <div className="text-2xl font-bold mb-2">Document not found</div>
            <Link to="/legal" className="text-emerald-600 hover:underline">Back to all documents</Link>
          </div>
        </div>
      );
    }
    return <PublicSummaryDocument slug={slug} summary={summary} docTitle={fullDoc?.title || summary.title} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Nav bar */}
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold tracking-tight">{COMPANY.name}</span>
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600" data-testid="legal-back-home">Back to home →</Link>
        </div>
      </nav>

      {/* Header */}
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-2">Legal · Compliance · Transparency</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Legal center</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Everything about how {COMPANY.name} operates, what we do (and don’t do) with your data,
          how money moves, and what you can and cannot ship. All documents effective {EFFECTIVE_DATE}.
        </p>

        {/* Contact strip */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <ContactCell icon={FileText} label="Legal"       value={COMPANY.legalEmail} />
          <ContactCell icon={Eye}      label="Privacy"     value={COMPANY.privacyEmail} />
          <ContactCell icon={Scale}    label="Compliance"  value={COMPANY.complianceEmail} />
        </div>

        {/* Doc grid — PUBLIC SUMMARIES */}
        <div className="mt-6 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20 p-4 flex items-start gap-3">
          <Shield className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700 dark:text-slate-300">
            <div className="font-semibold">Public vs. Member documents</div>
            <div className="mt-1">
              This page shows <strong>plain-language public summaries</strong>. The complete legally-binding
              agreements you actually sign are available inside your account at{' '}
              <Link to="/dashboard/legal" className="text-emerald-600 hover:underline font-semibold">/dashboard/legal</Link>{' '}
              (login required).
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {LEGAL_DOCS.map((doc) => {
            const Icon  = ICON_BY_SLUG[doc.slug] || FileText;
            const color = COLOR_BY_SLUG[doc.slug] || 'from-slate-500 to-slate-700';
            const summary = PUBLIC_SUMMARIES[doc.slug];
            return (
              <Link key={doc.slug} to={`/legal/${doc.slug}`} data-testid={`legal-doc-card-${doc.slug}`}>
                <Card className="hover:border-emerald-400 transition-all hover:-translate-y-0.5 cursor-pointer h-full">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-full px-2 py-0.5">Public summary</span>
                    </div>
                    <div className="font-semibold text-slate-900 dark:text-white">{doc.title}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{summary?.tagline || doc.intro?.slice(0, 140)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-600 mt-3 font-semibold">Read summary →</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Role of SelleSend disclosure card */}
        <div className="mt-10 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-white dark:bg-slate-900 p-6">
          <div className="font-bold text-lg mb-2">Role of {COMPANY.name}</div>
          {ROLE_STATEMENT.map((parrafo, i) => (
            <p key={i} className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-2 last:mb-0">
              {parrafo}
            </p>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-800 mt-10">
        <div className="max-w-5xl mx-auto px-5 py-5 text-[11px] text-slate-500 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
          <span>{COMPANY.address1} · {COMPANY.address2} · {COMPANY.phone}</span>
        </div>
      </div>
    </div>
  );
}

function ContactCell({ icon: Icon, label, value }) {
  return (
    <a href={`mailto:${value}`} className="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 hover:border-emerald-400">
      <Icon className="w-3.5 h-3.5 text-emerald-600" />
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 dark:text-slate-200 ml-auto">{value}</span>
    </a>
  );
}
